import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  useAcknowledgeAlertNotifications,
  useAlertSummary,
} from '@src/features/alerts';
import { useAuthentication } from '@src/features/auth';
import { useTaskSummary } from '@src/features/tasks';
import Badge from '@src/shared/ui/Badge';
import IconButton from '@src/shared/ui/Button/IconButton';
import Link from '@src/shared/ui/Link';
import { Box } from '@src/shared/ui/layout';
import {
  MailIcon,
  NotificationsIcon,
  SearchIcon,
  TasksIcon,
} from '@src/shared/ui/icons';

const StyleSheet = {
  root: {
    alignSelf: 'start',
    color: 'common.black',
    display: 'flex',
    gap: '5px',
    '& .MuiIconButton-root:hover, & .MuiIconButton-root.Mui-active': {
      backgroundColor: 'grey.600',
      color: 'common.black',
    },
  },
};

export default function TopMenu() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const isAlertsActive = pathname.startsWith('/alerts');
  const isTasksActive = pathname.startsWith('/tasks');
  const { data: alertSummary } = useAlertSummary(user?.id);
  const { data: taskSummary } = useTaskSummary();
  const { mutate: acknowledgeAlertNotifications } =
    useAcknowledgeAlertNotifications(user?.id);
  const ownAlertsCount = alertSummary?.own_open_alerts ?? 0;
  const deputedAlertsCount = alertSummary?.deputed_open_alerts ?? 0;
  const managedAlertsCount = alertSummary?.managed_open_alerts ?? 0;
  const actionableAlertsCount = ownAlertsCount + deputedAlertsCount;
  const alertBadgeCount = actionableAlertsCount || managedAlertsCount;
  const tasksBadgeCount =
    (taskSummary?.overdue_tasks ?? 0) +
    (taskSummary?.due_tasks ?? 0) +
    (taskSummary?.pending_tasks ?? 0);

  return (
    <Box sx={StyleSheet.root}>
      <IconButton color="inherit" disabled tooltipTitle={t('common.search', 'Search')}>
        <SearchIcon color="inherit" />
      </IconButton>
      <IconButton
        color="inherit"
        component={Link}
        to="/alerts"
        className={isAlertsActive ? 'Mui-active' : undefined}
        tooltipTitle={t('alerts.page_title', 'Alerts')}
        draggable={false}
        onClick={() => acknowledgeAlertNotifications()}
      >
        <Badge
          badgeContent={alertBadgeCount}
          color={actionableAlertsCount > 0 ? 'primary' : 'secondary'}
          invisible={alertBadgeCount === 0}
        >
          <NotificationsIcon color="inherit" />
        </Badge>
      </IconButton>
      <IconButton color="inherit" disabled tooltipTitle={t('common.mail', 'Mail')}>
        <Badge badgeContent={13} color="primary" invisible={true}>
          <MailIcon color="inherit" />
        </Badge>
      </IconButton>
      <IconButton
        color="inherit"
        component={Link}
        to="/tasks"
        className={isTasksActive ? 'Mui-active' : undefined}
        tooltipTitle={t('common.tasks', 'Tasks')}
        draggable={false}
      >
        <Badge
          badgeContent={tasksBadgeCount}
          color="primary"
          invisible={tasksBadgeCount === 0}
        >
          <TasksIcon color="inherit" />
        </Badge>
      </IconButton>
    </Box>
  );
}
